import { Cell, CellKind, Grid, headingOffset } from "@highmarch/domain";

/**
 * A plausible road for a level's ground, drawn without the card draft: the title's diorama (titleDiorama.ts) lays it
 * for its staged battle, and the level-load war map (ui/levelLoading.ts) inks it while the level's files come in.
 * In play the road is the player's own (road cards, DESIGN §4.3), so neither can show THE road — this is what a road
 * on that ground could look like: out of the fortress gate, straight runs of two to four cells with turns between,
 * never touching itself, over a gorge only straight and at most three cells (the plank bridges), heading away from
 * the fortress. Seeded, so the same level always sketches the same road.
 *
 * Pure (domain grid in, cells out), so it is unit-tested without a browser.
 */
export interface RoadSketch {
  /** Fortress gate outward: the first cell is the one in front of the gate, the last is where the portal opens. */
  readonly cells: readonly Cell[];
  /** True where the cell is a gorge the road bridges. */
  readonly bridge: readonly boolean[];
}

/** Longest straight gorge crossing (RoadMap.MaxBridge). */
const MaxBridge = 3;
/** Search nodes before the longest road found so far is taken. */
const MaxNodes = 6000;

/** mulberry32: a small seeded generator (presentation only, never the sim's). */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** How long a road to aim for on this grid: most of a phone screen's worth of play, bounded by the open ground. */
export function sketchLength(grid: Grid): number {
  let open = 0;
  for (let i = 0; i < grid.cellCount; i++) if (grid.kindAt(grid.cellAt(i)) === CellKind.Empty) open++;
  return Math.max(6, Math.min(26, Math.round(open * 0.22)));
}

export function sketchRoad(grid: Grid, seed: number, length = sketchLength(grid)): RoadSketch {
  const random = rng(seed);
  const used = new Uint8Array(grid.cellCount);
  const base = grid.base;
  const first = base.step(grid.baseHeading);
  const free = (c: Cell): boolean => grid.inBounds(c) && used[grid.index(c)] === 0;
  // a road cell may touch (4-way) only the cell it came from: no blobs, no road running alongside itself
  const lonely = (c: Cell, from: Cell): boolean => {
    for (const h of [0, 1, 2, 3]) {
      const [dx, dy] = headingOffset(h);
      const n = new Cell(c.x + dx, c.y + dy);
      if (n.equals(from) || !grid.inBounds(n)) continue;
      if (used[grid.index(n)] !== 0) return false;
      if (grid.kindAt(n) === CellKind.Base && !from.equals(base)) return false;
    }
    return true;
  };
  const path: Cell[] = [];
  const bridge: boolean[] = [];
  let best: { cells: Cell[]; bridge: boolean[] } = { cells: [], bridge: [] };
  let nodes = 0;
  const far = (c: Cell): number => Math.abs(c.x - base.x) + Math.abs(c.y - base.y);

  /** The cells one move lays from `at` heading `h`: one ground cell, or a straight crossing and its far bank. */
  const move = (at: Cell, h: number): Cell[] | null => {
    const [dx, dy] = headingOffset(h);
    const out: Cell[] = [];
    let c = at;
    for (let k = 0; k <= MaxBridge; k++) {
      c = new Cell(c.x + dx, c.y + dy);
      if (!free(c)) return null;
      const kind = grid.kindAt(c);
      if (kind === CellKind.Empty) { out.push(c); return out; }
      if (kind !== CellKind.Chasm) return null;
      out.push(c);
    }
    return null;                                            // a gorge wider than a bridge
  };

  const walk = (heading: number, run: number, want: number): boolean => {
    if (++nodes > MaxNodes) return true;
    if (path.length > best.cells.length) best = { cells: [...path], bridge: [...bridge] };
    if (path.length >= length) return true;
    const at = path[path.length - 1];
    const turns = [heading + 1, heading + 3].map((h) => h & 3);
    if (random() < 0.5) turns.reverse();
    // straight until the run is long enough, then a turn (the one heading away from the fortress first)
    turns.sort((a, b) => {
      const [ax, ay] = headingOffset(a), [bx, by] = headingOffset(b);
      return far(new Cell(at.x + bx, at.y + by)) - far(new Cell(at.x + ax, at.y + ay));
    });
    const order = run < want ? [heading, ...turns] : [...turns, heading];
    for (const h of order) {
      const cells = move(at, h);
      if (!cells) continue;
      let prev = at, ok = true;
      for (const c of cells) { if (!lonely(c, prev)) { ok = false; break; } prev = c; }
      if (!ok) continue;
      for (const c of cells) { used[grid.index(c)] = 1; path.push(c); bridge.push(grid.kindAt(c) === CellKind.Chasm); }
      const straight = h === heading;
      if (walk(h, straight ? run + cells.length : cells.length, straight ? want : 2 + Math.floor(random() * 3))) return true;
      for (let k = 0; k < cells.length; k++) { used[grid.index(path.pop()!)] = 0; bridge.pop(); }
    }
    return false;
  };

  if (grid.inBounds(first) && grid.kindAt(first) === CellKind.Empty) {
    used[grid.index(first)] = 1;
    path.push(first); bridge.push(false);
    walk(grid.baseHeading, 1, 2 + Math.floor(random() * 3));
  }
  // never end on a bridge: the portal opens on ground
  const { cells, bridge: br } = best;
  let n = cells.length;
  while (n > 0 && br[n - 1]) n--;
  return { cells: cells.slice(0, n), bridge: br.slice(0, n) };
}
